import office from '@/taskpane/officejs/office-wrapper'
import state from '@/taskpane/state/sidebar-state'
import PersonInfo from '../models/person-info'
import EmailInfo from '../models/mail-info'
import sendMessageToSidebarWithIds from './send-message-to-sidebar-with-ids'
import CallbackArgs, { defaultCallbackArgs } from './callback-args'
import configuration from '@/taskpane/common/configuration'
import CustomEmailData from '../models/custom-email-data'
import { observeMailComposeChanges } from '../observers/observe-compose-changes'
import { officeApiItem_GetConversationIndexAsync } from '@/taskpane/officejs/async/office-api-mailbox-item-async'

export interface MailItemOpenArgs extends CallbackArgs {
  id: string
  subject: string
  people: PersonInfo[]
  threadId: string
  threadIndex: string
  threads: EmailInfo[]
  linkedIds: string[]
  conversationId: string
  headerMessageId: string
}

export default async function sendMailItemOpenMessage() {
  let mailArgs = await getMailItemOpenArgs()
  if (mailArgs) {
    mailArgs = await sendMessageToSidebarWithIds('MailItemOpen', mailArgs, mailArgs.subject, mailArgs.people)
  }

  if (office.mode.isCompose() && !state.currentItem.composeInfo.observersInitialized) {
    observeMailComposeChanges(mailArgs)
  }

  if (!state.mailboxProcessingStarted) {
    state.startMailboxProcessing()
  }
}

export async function getMailItemOpenArgs(): Promise<MailItemOpenArgs> {
  if (!Office.context.mailbox.item) {
    return null
  }

  const [emailInfo, people] = await Promise.all([getEmailInfo(), office.people.get()])

  return {
    ...defaultCallbackArgs(),
    threadId: emailInfo.threadId,
    threadIndex: emailInfo.threadIndex,
    people: people as PersonInfo[],
    threads: [emailInfo],
    linkedIds: [],
    subject: emailInfo.subject,
    headerMessageId: emailInfo.headerMessageId,
  } as MailItemOpenArgs
  // args.id and args.threadId will be set later by sendItemOpenMessageWithIds
}

async function getEmailInfo(): Promise<EmailInfo> {
  const [subject, from, to, cc, bcc, isSentItem, attachments, categories, guid, replyTrackingId, conversationIndex] = await Promise.all([
    office.subject.get(),
    office.mail.from(),
    office.mail.to(),
    office.mail.cc(),
    office.mail.bcc(),
    office.mail.isSentItem(),
    office.attachments.get(),
    office.categories.get(),
    office.customProps.getTrackingId(),
    office.customProps.getReplyTrackingId(),
    officeApiItem_GetConversationIndexAsync(),
  ])

  const customData: CustomEmailData[] = []
  if (from?.emailAddress && from.emailAddress.toLowerCase() === configuration.EMAIL_BLAST_SENDER_ADDRESS) {
    const emailBlastId = await office.body.getEmailBlastId()
    customData.push({ key: 'EmailBlastId', value: emailBlastId })
  }

  const timeSent = office.mail.dateTimeSent() || new Date()

  return {
    attachments: attachments,
    bcc: bcc,
    body: '',
    categories: categories,
    cc: cc,
    composing: office.mode.isCompose(),
    datetime: timeSent,
    from: [from],
    headerMessageId: office.mail.internetMessageId(),
    id: Office.context.mailbox.item.itemId,
    sentItem: isSentItem,
    to: to,
    subject: subject,
    threadId: office.mail.conversationId(),
    threadIndex: conversationIndex,
    timestamp: timeSent.getTime(),
    uuid: guid,
    replyTrackingId: replyTrackingId,
    customEmailData: customData,
  }
}
